import React, { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import AppIcon from '../../../components/AppIcon';
import Toast from '../../../components/ui/Toast';
import {
  backfillDronePermitFromStorage,
  getDroneExpiryBadgeClass,
  getDroneExpiryStatus,
  getDroneExpiryStatusLabel,
  getDronePermitStatus,
  getDronePermitStatusLabel,
  isIdentityVerificationFile,
  photographerUsesDrone,
  uploadDronePermit,
  validateDronePermitFile,
} from '../../../services/dronePermitService';
import {
  backfillFreelancePermitFromStorage,
  getFreelanceExpiryBadgeClass,
  getFreelanceExpiryStatus,
  getFreelanceExpiryStatusLabel,
  getFreelancePermitStatus,
  getFreelancePermitStatusLabel,
  photographerIsIndividual,
  uploadFreelancePermit,
  validateFreelancePermitFile,
} from '../../../services/freelancePermitService';
import {
  getIdentityExpiryBadgeClass,
  getIdentityExpiryStatus,
  getIdentityExpiryStatusLabel,
  getIdentityVerificationStatus,
  getIdentityVerificationStatusLabel,
} from '../../../services/identityDocumentService';

const CERT_ALLOWED = ['application/pdf', 'image/jpeg', 'image/png'];
const CERT_MAX_SIZE = 5 * 1024 * 1024;

const STATUS_BADGE_CLASS = {
  green: 'bg-green-100 text-green-700',
  blue: 'bg-blue-100 text-blue-700',
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-800',
  slate: 'bg-slate-100 text-slate-600',
};

const UPLOAD_BTN_CLASS =
  'cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition shrink-0';
const ACTION_BTN_CLASS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50 hover:border-slate-300 transition shrink-0 disabled:opacity-50 disabled:pointer-events-none';

const StatusBadge = ({ label, tone = 'slate' }) => (
  <span className={`text-xs font-bold px-2.5 py-1 rounded-lg shrink-0 ${STATUS_BADGE_CLASS[tone] || STATUS_BADGE_CLASS.slate}`}>
    {label}
  </span>
);

const getDocStatusTone = (status) => {
  if (status === 'approved') return 'green';
  if (status === 'submitted') return 'blue';
  if (status === 'rejected') return 'red';
  return 'amber';
};

const VerificationAccordionItem = ({
  icon,
  title,
  hint,
  required = true,
  statusLabel,
  statusTone,
  open,
  onToggle,
  children,
}) => (
  <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm" dir="rtl">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-3 p-4 text-start hover:bg-slate-50/80 transition-colors"
      aria-expanded={open}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
        statusTone === 'green' ? 'bg-green-100 text-green-700' :
        statusTone === 'red' ? 'bg-red-100 text-red-700' :
        statusTone === 'blue' ? 'bg-blue-100 text-blue-700' :
        'bg-slate-100 text-slate-600'
      }`}>
        <AppIcon name={statusTone === 'green' ? 'Check' : icon} size={20} />
      </div>
      <div className="flex-1 min-w-0 text-start">
        <div className="flex items-center justify-start gap-2 flex-wrap">
          <p className="font-bold text-slate-900">{title}</p>
          {!required && (
            <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">اختياري</span>
          )}
        </div>
        {hint && <p className="text-xs text-slate-500 mt-0.5 truncate text-start">{hint}</p>}
      </div>
      <StatusBadge label={statusLabel} tone={statusTone} />
      <AppIcon name={open ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-slate-400 shrink-0" />
    </button>
    {open && (
      <div className="px-4 pb-4 pt-3 border-t border-slate-100">
        {children}
      </div>
    )}
  </div>
);

const IdentityVerificationSection = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);

  // Certificates state
  const [certificates, setCertificates] = useState([]);
  const [uploadingCerts, setUploadingCerts] = useState(false);
  const [deletingCertIdx, setDeletingCertIdx] = useState(null);
  const certInputRef = useRef(null);

  // Freelance permit state (individual accounts only)
  const [isIndividual, setIsIndividual] = useState(false);
  const [freelanceUploading, setFreelanceUploading] = useState(false);
  const [freelancePermitData, setFreelancePermitData] = useState({
    url: null,
    status: 'not_uploaded',
    rejectionReason: null,
    expiresAt: null,
    expiryStatus: 'no_date',
  });
  const freelanceInputRef = useRef(null);

  // Drone permit state
  const [hasDrone, setHasDrone] = useState(false);
  const [droneUploading, setDroneUploading] = useState(false);
  const [dronePermitData, setDronePermitData] = useState({
    url: null,
    status: 'not_uploaded',
    rejectionReason: null,
    expiresAt: null,
    expiryStatus: 'no_date',
  });
  const droneInputRef = useRef(null);
  const [verificationData, setVerificationData] = useState({
    status: 'pending',
    identityDocStatus: 'not_uploaded',
    identityVerified: false,
    accountActivated: false,
    portfolioVerified: false,
    certificatesVerified: false,
    documents: [],
    rejectionReason: null,
    identityRejectionReason: null,
    verificationNotes: null,
    expiresAt: null,
    expiryStatus: 'no_date',
  });
  const fileInputRef = useRef(null);
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    freelance: true,
    drone: true,
    certificates: false,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Load verification status
  const loadVerificationStatus = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      // Get photographer profile verification data (certificates_verified بعد تشغيل هجرة 20260224100000)
      let profile = null;
      let profileError = null;
      const selectWithCert = `
          verification_status,
          status,
          identity_verified,
          portfolio_verified,
          id_document_url,
          id_document_type,
          verification_submitted_at,
          verification_notes,
          rejection_reason,
          identity_rejection_reason,
          identity_reviewed_at,
          certificates_verified,
          identity_document_expires_at
        `;
      const selectWithoutCert = `
          verification_status,
          status,
          identity_verified,
          portfolio_verified,
          id_document_url,
          id_document_type,
          verification_submitted_at,
          verification_notes,
          rejection_reason,
          identity_rejection_reason,
          identity_reviewed_at,
          identity_document_expires_at
        `;
      let result = await supabase.from('photographer_profiles').select(selectWithCert).eq('user_id', userId).single();
      profile = result.data;
      profileError = result.error;
      if (profileError) {
        result = await supabase.from('photographer_profiles').select(selectWithoutCert).eq('user_id', userId).single();
        profile = result.data;
        profileError = result.error;
      }
      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      // Get uploaded documents from storage
      const { data: files } = await supabase.storage
        .from('verification-docs')
        .list(userId, {
          limit: 10,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      // Build identity document URLs (exclude drone permits)
      const documents = [];
      if (files && files.length > 0) {
        for (const file of files) {
          if (!isIdentityVerificationFile(file.name)) continue;
          const { data: urlData } = supabase.storage
            .from('verification-docs')
            .getPublicUrl(`${userId}/${file.name}`);
          
          documents.push({
            name: file.name,
            url: urlData?.publicUrl,
            type: file.metadata?.mimetype || 'unknown',
            uploadedAt: file.created_at
          });
        }
      }

      const identityDocStatus = getIdentityVerificationStatus({
        ...profile,
        identityDocuments: documents,
      });
      setVerificationData({
        status: profile?.verification_status || 'pending',
        identityDocStatus,
        identityVerified: profile?.identity_verified || false,
        accountActivated: profile?.status === 'approved' || profile?.verification_status === 'approved',
        portfolioVerified: profile?.portfolio_verified || false,
        certificatesVerified: profile?.certificates_verified || false,
        documents,
        rejectionReason: profile?.rejection_reason,
        identityRejectionReason: profile?.identity_rejection_reason || null,
        verificationNotes: profile?.verification_notes,
        idDocumentUrl: profile?.id_document_url,
        idDocumentType: profile?.id_document_type,
        submittedAt: profile?.verification_submitted_at,
        expiresAt: profile?.identity_document_expires_at || null,
        expiryStatus: profile?.identity_verified
          ? getIdentityExpiryStatus(profile?.identity_document_expires_at)
          : 'no_date',
      });

      // Load certificates, entity type, shooting methods, permits from profile
      const { data: profileFull } = await supabase
        .from('photographer_profiles')
        .select(`
          certificates,
          entity_type,
          shooting_methods,
          freelance_permit_url,
          freelance_permit_verified,
          freelance_permit_submitted_at,
          freelance_permit_reviewed_at,
          freelance_permit_rejection_reason,
          freelance_permit_expires_at,
          drone_permit_url,
          drone_permit_verified,
          drone_permit_submitted_at,
          drone_permit_reviewed_at,
          drone_permit_rejection_reason,
          drone_permit_expires_at
        `)
        .eq('user_id', userId)
        .single();

      setCertificates(Array.isArray(profileFull?.certificates) ? profileFull.certificates : []);
      setIsIndividual(photographerIsIndividual(profileFull?.entity_type));
      const methods = Array.isArray(profileFull?.shooting_methods) ? profileFull.shooting_methods : [];
      setHasDrone(photographerUsesDrone(methods));

      let freelanceProfile = profileFull;
      if (photographerIsIndividual(profileFull?.entity_type) && !profileFull?.freelance_permit_url) {
        const backfilledFreelanceUrl = await backfillFreelancePermitFromStorage(supabase, userId, files);
        if (backfilledFreelanceUrl) {
          freelanceProfile = { ...profileFull, freelance_permit_url: backfilledFreelanceUrl };
        }
      }

      const freelanceStatus = getFreelancePermitStatus(freelanceProfile);
      setFreelancePermitData({
        url: freelanceProfile?.freelance_permit_url || null,
        status: freelanceStatus,
        rejectionReason: freelanceProfile?.freelance_permit_rejection_reason || null,
        expiresAt: freelanceProfile?.freelance_permit_expires_at || null,
        expiryStatus: freelanceStatus === 'approved'
          ? getFreelanceExpiryStatus(freelanceProfile?.freelance_permit_expires_at)
          : 'no_date',
      });

      let droneProfile = profileFull;
      if (photographerUsesDrone(methods) && !profileFull?.drone_permit_url) {
        const backfilledUrl = await backfillDronePermitFromStorage(supabase, userId, files);
        if (backfilledUrl) {
          droneProfile = {
            ...profileFull,
            drone_permit_url: backfilledUrl,
            drone_permit_submitted_at: profileFull?.drone_permit_submitted_at || new Date().toISOString(),
          };
        }
      }

      const droneStatus = getDronePermitStatus(droneProfile);
      setDronePermitData({
        url: droneProfile?.drone_permit_url || null,
        status: droneStatus,
        rejectionReason: droneProfile?.drone_permit_rejection_reason || null,
        expiresAt: droneProfile?.drone_permit_expires_at || null,
        expiryStatus: droneStatus === 'approved'
          ? getDroneExpiryStatus(droneProfile?.drone_permit_expires_at)
          : 'no_date',
      });

    } catch (error) {
      console.error('Error loading verification status:', error);
      setToast({ type: 'error', message: 'فشل تحميل بيانات التحقق' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVerificationStatus();
  }, [userId]);

  useEffect(() => {
    if (loading) return;
    setExpandedSections({
      identity: verificationData.identityDocStatus === 'rejected' || verificationData.identityDocStatus === 'not_uploaded',
      freelance: isIndividual && (freelancePermitData.status === 'rejected' || freelancePermitData.status === 'not_uploaded'),
      drone: hasDrone && (dronePermitData.status === 'rejected' || dronePermitData.status === 'not_uploaded'),
      certificates: false,
    });
  }, [loading, verificationData.identityDocStatus, isIndividual, freelancePermitData.status, hasDrone, dronePermitData.status]);

  // Upload document
  const handleUploadDocument = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setToast({ type: 'error', message: 'نوع الملف غير مدعوم. استخدم JPEG, PNG, WebP أو PDF' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setToast({ type: 'error', message: 'حجم الملف كبير جداً. الحد الأقصى 10MB' });
      return;
    }

    setUploading(true);
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const ext = file.name.split('.').pop();
      const fileName = `id_document_${timestamp}.${ext}`;
      const filePath = `${userId}/${fileName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('verification-docs')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('verification-docs')
        .getPublicUrl(filePath);

      // Update photographer profile
      const { error: updateError } = await supabase
        .from('photographer_profiles')
        .update({
          id_document_url: urlData?.publicUrl,
          id_document_type: file.type.includes('pdf') ? 'pdf' : 'image',
          verification_status: 'submitted',
          verification_submitted_at: new Date().toISOString(),
          identity_verified: false,
          identity_document_expires_at: null,
          identity_reviewed_at: null,
          identity_rejection_reason: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      // 🔔 Create admin notification for the new verification request
      try {
        // Get photographer name for the notification
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('full_name, email')
          .eq('id', userId)
          .single();

        const photographerName = userProfile?.full_name || userProfile?.email || 'مصور';
        const docType = file.type.includes('pdf') ? 'PDF' : 'صورة';

        await supabase
          .from('admin_notifications')
          .insert({
            type: 'verification_request',
            title: '📄 طلب توثيق هوية جديد',
            message: `المصور "${photographerName}" رفع مستند تحقق من الهوية (${docType}) ويحتاج مراجعة.`,
            related_user_id: userId,
            related_data: {
              photographer_name: photographerName,
              photographer_email: userProfile?.email,
              document_url: urlData?.publicUrl,
              document_type: docType,
              submitted_at: new Date().toISOString()
            }
          });

        console.log('✅ Admin notification created for verification request');
      } catch (notifError) {
        // Non-critical: don't fail the upload if notification fails
        console.warn('⚠️ Failed to create admin notification:', notifError);
      }

      setToast({ type: 'success', message: '✅ تم رفع المستند بنجاح وإرساله للمراجعة' });
      loadVerificationStatus(); // Reload data

    } catch (error) {
      console.error('Error uploading document:', error);
      setToast({ type: 'error', message: `فشل رفع المستند: ${error.message}` });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Delete document
  const handleDeleteDocument = async (fileName) => {
    if (!userId || !window.confirm('هل أنت متأكد من حذف هذا المستند؟')) return;

    try {
      const { error } = await supabase.storage
        .from('verification-docs')
        .remove([`${userId}/${fileName}`]);

      if (error) throw error;

      // Update profile to remove document URL if it matches
      await supabase
        .from('photographer_profiles')
        .update({
          id_document_url: null,
          id_document_type: null,
          verification_status: 'pending',
          identity_verified: false,
          identity_document_expires_at: null,
          identity_reviewed_at: null,
          identity_rejection_reason: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      setToast({ type: 'success', message: 'تم حذف المستند' });
      loadVerificationStatus();

    } catch (error) {
      console.error('Error deleting document:', error);
      setToast({ type: 'error', message: 'فشل حذف المستند' });
    }
  };

  // Upload freelance work permit
  const handleFreelancePermitUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !userId) return;

    const validationError = validateFreelancePermitFile(file);
    if (validationError) {
      setToast({ type: 'error', message: validationError });
      return;
    }

    setFreelanceUploading(true);
    try {
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('full_name, email')
        .eq('id', userId)
        .single();

      const photographerName = userProfile?.full_name || userProfile?.email || 'مصور';
      const { publicUrl } = await uploadFreelancePermit(supabase, {
        userId,
        file,
        photographerName,
        previousUrl: freelancePermitData.url,
      });

      setFreelancePermitData({
        url: publicUrl,
        status: 'submitted',
        rejectionReason: null,
        expiresAt: null,
        expiryStatus: 'no_date',
      });
      setToast({ type: 'success', message: '✅ تم رفع وثيقة العمل الحر وإرسالها للمراجعة' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'فشل رفع الوثيقة' });
    } finally {
      setFreelanceUploading(false);
      if (freelanceInputRef.current) freelanceInputRef.current.value = '';
    }
  };

  // Upload drone permit
  const handleDronePermitUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !userId) return;

    const validationError = validateDronePermitFile(file);
    if (validationError) {
      setToast({ type: 'error', message: validationError });
      return;
    }

    setDroneUploading(true);
    try {
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('full_name, email')
        .eq('id', userId)
        .single();

      const photographerName = userProfile?.full_name || userProfile?.email || 'مصور';
      const { publicUrl } = await uploadDronePermit(supabase, {
        userId,
        file,
        photographerName,
        previousUrl: dronePermitData.url,
      });

      setDronePermitData({
        url: publicUrl,
        status: 'submitted',
        rejectionReason: null,
        expiresAt: null,
        expiryStatus: 'no_date',
      });
      setToast({ type: 'success', message: '✅ تم رفع تصريح الدرون وإرساله للمراجعة' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'فشل رفع التصريح' });
    } finally {
      setDroneUploading(false);
      if (droneInputRef.current) droneInputRef.current.value = '';
    }
  };

  // Upload professional certificate
  const handleCertUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!CERT_ALLOWED.includes(file.type)) { setToast({ type: 'error', message: 'يُقبل PDF، JPG، PNG فقط' }); return; }
    if (file.size > CERT_MAX_SIZE) { setToast({ type: 'error', message: 'الحد الأقصى 5MB' }); return; }
    setUploadingCerts(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
      const filePath = `${userId}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage.from('photographer-certificates').upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('photographer-certificates').getPublicUrl(filePath);
      const nextList = [...certificates, { url: urlData?.publicUrl || '', name: file.name }];
      const { error: updateErr } = await supabase.from('photographer_profiles').update({ certificates: nextList, updated_at: new Date().toISOString() }).eq('user_id', userId);
      if (updateErr) throw updateErr;
      setCertificates(nextList);
      setToast({ type: 'success', message: 'تم رفع الشهادة بنجاح' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'فشل رفع الشهادة' });
    } finally {
      setUploadingCerts(false);
      if (certInputRef.current) certInputRef.current.value = '';
    }
  };

  // Delete professional certificate
  const handleCertDelete = async (index) => {
    const item = certificates[index];
    if (!item?.url) return;
    const path = item.url.includes('photographer-certificates/') ? item.url.split('photographer-certificates/')[1] : null;
    setDeletingCertIdx(index);
    try {
      if (path) await supabase.storage.from('photographer-certificates').remove([path]);
      const nextList = certificates.filter((_, i) => i !== index);
      await supabase.from('photographer_profiles').update({ certificates: nextList.length ? nextList : null, updated_at: new Date().toISOString() }).eq('user_id', userId);
      setCertificates(nextList);
      setToast({ type: 'success', message: 'تم حذف الشهادة' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'فشل حذف الشهادة' });
    } finally {
      setDeletingCertIdx(null);
    }
  };

  const requiredDocuments = useMemo(() => {
    const docs = [
      { key: 'identity', label: 'مستند الهوية', complete: verificationData.identityVerified },
    ];
    if (isIndividual) {
      docs.push({ key: 'freelance', label: 'وثيقة العمل الحر', complete: freelancePermitData.status === 'approved' });
    }
    if (hasDrone) {
      docs.push({ key: 'drone', label: 'تصريح الدرون', complete: dronePermitData.status === 'approved' });
    }
    return docs;
  }, [verificationData.identityVerified, isIndividual, freelancePermitData.status, hasDrone, dronePermitData.status]);

  const completedRequiredCount = requiredDocuments.filter((d) => d.complete).length;
  const totalRequiredCount = requiredDocuments.length;
  const progressPercent = totalRequiredCount > 0 ? Math.round((completedRequiredCount / totalRequiredCount) * 100) : 0;
  const nextIncompleteDoc = requiredDocuments.find((d) => !d.complete);
  const allRequiredApproved = totalRequiredCount > 0 && completedRequiredCount === totalRequiredCount;
  const awaitingActivation = allRequiredApproved && !verificationData.accountActivated;

  const accountStatusLabel = verificationData.accountActivated
    ? 'حساب مُفعَّل ✓'
    : awaitingActivation
      ? 'بانتظار التفعيل'
      : `${completedRequiredCount} من ${totalRequiredCount} مكتمل`;
  const accountStatusTone = verificationData.accountActivated
    ? 'green'
    : awaitingActivation
      ? 'blue'
      : 'amber';

  const nextStepHint = verificationData.accountActivated
    ? 'حسابك موثّق ويظهر بشارة التوثيق للعملاء'
    : awaitingActivation
      ? 'اكتملت المستندات المطلوبة — بانتظار تفعيل حسابك من الإدارة'
      : nextIncompleteDoc
        ? `الخطوة التالية: ${nextIncompleteDoc.label}`
        : 'أكمل رفع المستندات المطلوبة';

  const alertItems = useMemo(() => {
    const items = [];
    if (verificationData.identityDocStatus === 'rejected' && verificationData.identityRejectionReason) {
      items.push({ tone: 'red', title: 'رفض مستند الهوية', message: verificationData.identityRejectionReason });
    }
    if (verificationData.status === 'rejected' && verificationData.rejectionReason) {
      items.push({ tone: 'red', title: 'رفض طلب التحقق', message: verificationData.rejectionReason });
    }
    if (freelancePermitData.status === 'rejected' && freelancePermitData.rejectionReason) {
      items.push({ tone: 'red', title: 'رفض وثيقة العمل الحر', message: freelancePermitData.rejectionReason });
    }
    if (dronePermitData.status === 'rejected' && dronePermitData.rejectionReason) {
      items.push({ tone: 'red', title: 'رفض تصريح الدرون', message: dronePermitData.rejectionReason });
    }
    if (verificationData.verificationNotes && verificationData.status === 'submitted') {
      items.push({ tone: 'blue', title: 'ملاحظات من الإدارة', message: verificationData.verificationNotes });
    }
    if (verificationData.status === 'pending' && verificationData.verificationNotes?.includes('انتهت صلاحية')) {
      items.push({
        tone: 'orange',
        title: 'انتهت صلاحية مستند الهوية',
        message: 'يرجى رفع هوية سارية لإعادة التوثيق واستئناف الظهور في المنصة',
      });
    }
    return items;
  }, [
    verificationData.identityDocStatus,
    verificationData.identityRejectionReason,
    verificationData.status,
    verificationData.rejectionReason,
    verificationData.verificationNotes,
    freelancePermitData.status,
    freelancePermitData.rejectionReason,
    dronePermitData.status,
    dronePermitData.rejectionReason,
  ]);

  const formatDocDate = (dateStr) => (
    dateStr
      ? new Date(dateStr).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })
      : null
  );

  const getIdentityHint = () => {
    if (verificationData.identityVerified && verificationData.expiresAt) return `ساري حتى ${formatDocDate(verificationData.expiresAt)}`;
    if (verificationData.identityDocStatus === 'rejected') return 'يرجى رفع مستند جديد';
    if (verificationData.identityDocStatus === 'submitted') return 'بانتظار مراجعة الإدارة';
    return 'ارفع هوية أو إقامة سارية';
  };

  const getFreelanceHint = () => {
    if (freelancePermitData.status === 'approved' && freelancePermitData.expiresAt) return `ساري حتى ${formatDocDate(freelancePermitData.expiresAt)}`;
    if (freelancePermitData.status === 'rejected') return 'يرجى رفع وثيقة جديدة';
    if (freelancePermitData.status === 'submitted') return 'بانتظار مراجعة الإدارة';
    return 'PDF أو JPG أو PNG — حتى 10MB';
  };

  const getDroneHint = () => {
    if (dronePermitData.status === 'approved' && dronePermitData.expiresAt) return `ساري حتى ${formatDocDate(dronePermitData.expiresAt)}`;
    if (dronePermitData.status === 'rejected') return 'يرجى رفع تصريح جديد';
    if (dronePermitData.status === 'submitted') return 'بانتظار مراجعة الإدارة';
    return 'PDF أو JPG أو PNG — حتى 10MB';
  };

  const certificatesHint = verificationData.certificatesVerified
    ? 'تم التحقق من شهاداتك'
    : certificates.length > 0
      ? `${certificates.length} شهادة مرفوعة — قيد مراجعة الإدارة`
      : 'تعزّز ملفك الاحترافي';

  const identityStatusLabel = verificationData.identityVerified
    ? 'مقبول'
    : getIdentityVerificationStatusLabel(verificationData.identityDocStatus);
  const identityStatusTone = verificationData.identityVerified ? 'green' : getDocStatusTone(verificationData.identityDocStatus);

  const certificatesStatusLabel = verificationData.certificatesVerified
    ? 'مقبولة'
    : certificates.length > 0
      ? 'قيد المراجعة'
      : 'لم تُرفع';
  const certificatesStatusTone = verificationData.certificatesVerified ? 'green' : certificates.length > 0 ? 'blue' : 'slate';

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-center py-8">
          <AppIcon name="Loader" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-start">
            <h2
              className="text-xl font-bold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
            >
              <AppIcon name="ShieldCheck" size={24} className="text-primary shrink-0" />
              التحقق والمستندات
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              وثّق حسابك لزيادة ثقة العملاء وظهورك في نتائج البحث
            </p>
          </div>
          <StatusBadge label={accountStatusLabel} tone={accountStatusTone} />
        </div>

        <div className="mt-4">
          <div className="flex justify-between text-xs text-slate-600 mb-1.5">
            <span>المستندات المطلوبة</span>
            <span>{completedRequiredCount} من {totalRequiredCount}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <p className="text-sm text-slate-600 mt-3">{nextStepHint}</p>

        {awaitingActivation && (
          <div className="mt-3 flex items-start gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl p-3">
            <AppIcon name="Clock" size={18} className="shrink-0 mt-0.5" />
            <p>اكتملت المستندات المطلوبة — سيتم تفعيل حسابك بعد مراجعة الإدارة</p>
          </div>
        )}
      </div>

      {alertItems.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-3">
          {alertItems.map((item, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 rounded-xl p-3 border ${
                item.tone === 'red' ? 'bg-red-50 border-red-200' :
                item.tone === 'blue' ? 'bg-blue-50 border-blue-200' :
                item.tone === 'orange' ? 'bg-orange-50 border-orange-200' :
                'bg-slate-50 border-slate-200'
              }`}
            >
              <AppIcon
                name={item.tone === 'red' ? 'AlertCircle' : item.tone === 'orange' ? 'AlertTriangle' : 'Info'}
                size={18}
                className={`shrink-0 mt-0.5 ${
                  item.tone === 'red' ? 'text-red-600' :
                  item.tone === 'blue' ? 'text-blue-600' :
                  item.tone === 'orange' ? 'text-orange-600' :
                  'text-slate-500'
                }`}
              />
              <div className="flex-1 min-w-0 text-start">
                <p className={`text-sm font-bold text-start ${
                  item.tone === 'red' ? 'text-red-800' :
                  item.tone === 'blue' ? 'text-blue-800' :
                  item.tone === 'orange' ? 'text-orange-800' :
                  'text-slate-800'
                }`}>
                  {item.title}
                </p>
                <p className={`text-sm mt-0.5 text-start ${
                  item.tone === 'red' ? 'text-red-700' :
                  item.tone === 'blue' ? 'text-blue-700' :
                  item.tone === 'orange' ? 'text-orange-700' :
                  'text-slate-600'
                }`}>
                  {item.message}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <VerificationAccordionItem
          icon="User"
          title="التحقق من الهوية"
          hint={getIdentityHint()}
          statusLabel={identityStatusLabel}
          statusTone={identityStatusTone}
          open={expandedSections.identity}
          onToggle={() => toggleSection('identity')}
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <p className="text-xs text-slate-500 flex-1 min-w-0">
              هوية أو إقامة، جواز، رخصة، أو سجل تجاري — صورة واضحة لجميع البيانات · JPEG، PNG، WebP، PDF (حتى 10MB)
            </p>
            {!verificationData.identityVerified && (
              <label className={`${UPLOAD_BTN_CLASS} ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUploadDocument}
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  disabled={uploading}
                />
                <AppIcon name="Upload" size={16} />
                {uploading ? 'جاري الرفع...' : 'رفع مستند'}
              </label>
            )}
          </div>

          {verificationData.identityVerified && verificationData.expiresAt && (
            <div className={`flex items-center gap-2 rounded-xl p-3 mb-4 border ${getIdentityExpiryBadgeClass(verificationData.expiryStatus)}`}>
              <AppIcon name="Calendar" size={16} className="shrink-0" />
              <p className="text-sm">
                ينتهي في {formatDocDate(verificationData.expiresAt)}
                {' — '}
                <span className="font-semibold">{getIdentityExpiryStatusLabel(verificationData.expiryStatus)}</span>
              </p>
            </div>
          )}

          {verificationData.documents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {verificationData.documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-white border border-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                      {doc.type?.includes('pdf') ? (
                        <AppIcon name="FileText" size={18} className="text-red-500" />
                      ) : doc.url ? (
                        <img src={doc.url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <AppIcon name="File" size={18} className="text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800 truncate">{doc.name}</p>
                      <p className="text-xs text-slate-500">
                        {doc.uploadedAt && formatDocDate(doc.uploadedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {doc.url && (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={ACTION_BTN_CLASS}
                      >
                        <AppIcon name="ExternalLink" size={14} />
                        عرض
                      </a>
                    )}
                    {!verificationData.identityVerified && (
                      <button
                        type="button"
                        onClick={() => handleDeleteDocument(doc.name)}
                        className={ACTION_BTN_CLASS}
                      >
                        <AppIcon name="Trash2" size={14} />
                        حذف
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !verificationData.identityVerified && (
              <p className="text-sm text-slate-500">لم ترفع مستند هوية بعد</p>
            )
          )}
        </VerificationAccordionItem>

        {isIndividual && (
          <VerificationAccordionItem
            icon="Briefcase"
            title="وثيقة العمل الحر"
            hint={getFreelanceHint()}
            statusLabel={getFreelancePermitStatusLabel(freelancePermitData.status)}
            statusTone={getDocStatusTone(freelancePermitData.status)}
            open={expandedSections.freelance}
            onToggle={() => toggleSection('freelance')}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <p className="text-xs text-slate-500 flex-1 min-w-0">
                وثيقة العمل الحر الصادرة من الجهة المختصة — PDF أو JPG أو PNG، حد أقصى 10 ميغابايت
              </p>
              <label className={`${UPLOAD_BTN_CLASS} ${freelanceUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <input ref={freelanceInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFreelancePermitUpload} disabled={freelanceUploading} />
                <AppIcon name="Upload" size={16} />
                {freelanceUploading ? 'جاري الرفع...' : freelancePermitData.url ? 'استبدال الوثيقة' : 'رفع الوثيقة'}
              </label>
            </div>

            {freelancePermitData.status === 'approved' && freelancePermitData.expiresAt && (
              <div className={`flex items-center gap-2 rounded-xl p-3 mb-4 border ${getFreelanceExpiryBadgeClass(freelancePermitData.expiryStatus)}`}>
                <AppIcon name="Calendar" size={16} className="shrink-0" />
                <p className="text-sm">
                  تنتهي في {formatDocDate(freelancePermitData.expiresAt)}
                  {' — '}
                  <span className="font-semibold">{getFreelanceExpiryStatusLabel(freelancePermitData.expiryStatus)}</span>
                </p>
              </div>
            )}

            {freelancePermitData.url ? (
              <div className="flex flex-wrap items-center gap-3 rounded-xl p-4 border border-slate-200 bg-slate-50">
                <AppIcon name="FileText" size={20} className="text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800">وثيقة العمل الحر</p>
                </div>
                <a
                  href={freelancePermitData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ACTION_BTN_CLASS}
                >
                  <AppIcon name="ExternalLink" size={14} />
                  عرض
                </a>
              </div>
            ) : (
              <p className="text-sm text-slate-500">لم ترفع وثيقة العمل الحر بعد</p>
            )}
          </VerificationAccordionItem>
        )}

        {hasDrone && (
          <VerificationAccordionItem
            icon="FileText"
            title="تصريح الطائرة المسيّرة (درون)"
            hint={getDroneHint()}
            statusLabel={getDronePermitStatusLabel(dronePermitData.status)}
            statusTone={getDocStatusTone(dronePermitData.status)}
            open={expandedSections.drone}
            onToggle={() => toggleSection('drone')}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <p className="text-xs text-slate-500 flex-1 min-w-0">
                تصريح تشغيل الدرون الصادر من الجهة المختصة — PDF، JPG، PNG، حد أقصى 10 ميغابايت
              </p>
              <label className={`${UPLOAD_BTN_CLASS} ${droneUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <input ref={droneInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleDronePermitUpload} disabled={droneUploading} />
                <AppIcon name="Upload" size={16} />
                {droneUploading ? 'جاري الرفع...' : dronePermitData.url ? (dronePermitData.status === 'approved' ? 'تحديث التصريح' : 'استبدال التصريح') : 'رفع التصريح'}
              </label>
            </div>

            {dronePermitData.status === 'approved' && dronePermitData.expiresAt && (
              <div className={`flex items-center gap-2 rounded-xl p-3 mb-4 border ${getDroneExpiryBadgeClass(dronePermitData.expiryStatus)}`}>
                <AppIcon name="Calendar" size={16} className="shrink-0" />
                <p className="text-sm">
                  ينتهي في {formatDocDate(dronePermitData.expiresAt)}
                  {' — '}
                  <span className="font-semibold">{getDroneExpiryStatusLabel(dronePermitData.expiryStatus)}</span>
                </p>
              </div>
            )}

            {dronePermitData.url ? (
              <div className="flex flex-wrap items-center gap-3 rounded-xl p-4 border border-slate-200 bg-slate-50">
                <AppIcon name="FileText" size={20} className="text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800">تصريح الدرون</p>
                </div>
                <a
                  href={dronePermitData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ACTION_BTN_CLASS}
                >
                  <AppIcon name="ExternalLink" size={14} />
                  عرض
                </a>
              </div>
            ) : (
              <p className="text-sm text-slate-500">لم ترفع تصريح الدرون بعد</p>
            )}
          </VerificationAccordionItem>
        )}

        <VerificationAccordionItem
          icon="Award"
          title="الشهادات الاحترافية"
          hint={certificatesHint}
          required={false}
          statusLabel={certificatesStatusLabel}
          statusTone={certificatesStatusTone}
          open={expandedSections.certificates}
          onToggle={() => toggleSection('certificates')}
        >
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <p className="text-xs text-slate-500 flex-1 min-w-0">
              شهادات التصوير الاحترافية — PDF، JPG، PNG، حد أقصى 5MB لكل ملف
            </p>
            <label className={`${UPLOAD_BTN_CLASS} ${uploadingCerts ? 'opacity-50 pointer-events-none' : ''}`}>
              <input ref={certInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={handleCertUpload} className="hidden" disabled={uploadingCerts} />
              <AppIcon name="Upload" size={16} />
              {uploadingCerts ? 'جاري الرفع...' : 'رفع شهادة'}
            </label>
          </div>

          {certificates.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {certificates.map((cert, i) => {
                const url = typeof cert === 'string' ? cert : cert?.url;
                const name = (typeof cert === 'object' && cert?.name) ? cert.name : `شهادة ${i + 1}`;
                return (
                  <div key={i} className="flex items-center justify-between gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <AppIcon name="FileText" size={18} className="text-slate-500 shrink-0" />
                      <span className="text-sm text-slate-800 truncate">{name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className={ACTION_BTN_CLASS}>
                          <AppIcon name="ExternalLink" size={14} />
                          عرض
                        </a>
                      )}
                      <button onClick={() => handleCertDelete(i)} disabled={deletingCertIdx === i} className={ACTION_BTN_CLASS}>
                        <AppIcon name="Trash2" size={14} />
                        {deletingCertIdx === i ? 'جاري...' : 'حذف'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-500">لم تقم بإضافة أي شهادات بعد</p>
          )}
        </VerificationAccordionItem>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default IdentityVerificationSection;
